import { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Loader2,
  ChevronDown,
  ChevronUp,
  Pencil,
  Save,
  FileText,
  FileCheck,
  Plus,
  Trash2,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { ColDef, CellValueChangedEvent, SelectionChangedEvent, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { supabase } from '@/integrations/supabase/client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { EntityStatusActions } from '@/components/status/EntityStatusActions';
import { UploadFieldTicketDialog } from '@/components/invoice/UploadFieldTicketDialog';
import { useAuth } from '@/contexts/AuthContext';
import { usePagination } from '@/hooks/usePagination';
import { cn } from '@/lib/utils';
import {
  invoiceService,
  invoiceItemService,
  priceBookItemService,
  profileService,
  vendorService,
} from '@/services';
import { calculateLineTotal } from '@prolarus/shared';
import type { InvoiceItem } from '@/services';
import type { PriceBookItem } from '@/services';
import type { ServiceContext } from '@prolarus/shared';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return '—'; }
};

export default function EditInvoice() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // UI state
  const [isHeaderOpen, setIsHeaderOpen] = useState(true);
  const [isHeaderEditing, setIsHeaderEditing] = useState(true);
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);
  const [lineItemsEditMode, setLineItemsEditMode] = useState(true);
  const [selectedLineItems, setSelectedLineItems] = useState<InvoiceItem[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showUploadTicketDialog, setShowUploadTicketDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pbSearchFocused, setPbSearchFocused] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState('line-items');
  const [leftPanelTab, setLeftPanelTab] = useState('pricebook');
  const [lineItemsSearch, setLineItemsSearch] = useState('');
  const [nonContractForm, setNonContractForm] = useState({
    item_code: '',
    description: '',
    quantity: 1,
    uom: '',
    unit_price: 0,
    discount: 0,
  });

  // Header form state
  const [headerForm, setHeaderForm] = useState({
    vendor_id: '' as string | null,
    invoice_number: '',
    invoice_date: '',
    service_date: '',
    payment_terms: '',
    po_number: '',
    notes: '',
  });
  const [headerSnapshot, setHeaderSnapshot] = useState<Record<string, unknown>>({});

  // Fetch invoice for edit
  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice-edit', id],
    queryFn: async () => {
      const result = await invoiceService.getForEdit(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Initialize form when invoice loads
  useEffect(() => {
    if (!invoice) return;

    setHeaderForm({
      vendor_id: invoice.vendor_id,
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || '',
      service_date: invoice.service_date || '',
      payment_terms: invoice.payment_terms || '',
      po_number: invoice.po_number || '',
      notes: invoice.notes || '',
    });
    setHeaderSnapshot({
      vendor_id: invoice.vendor_id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date,
      service_date: invoice.service_date,
      payment_terms: invoice.payment_terms,
      po_number: invoice.po_number,
      notes: invoice.notes,
    });

    if (invoice.current_status?.readonly) {
      setIsHeaderEditing(false);
      setLineItemsEditMode(false);
    }
  }, [invoice]);

  const isReadonly = invoice?.current_status?.readonly === true;

  // Fetch profile
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.get(user!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch vendors for picker
  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-for-edit', profile?.company_id],
    queryFn: async () => {
      const result = await vendorService.list(profile!.company_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!profile?.company_id,
  });

  // Fetch invoice items
  const { data: invoiceItems = [], isLoading: isLoadingItems } = useQuery({
    queryKey: ['invoice-items', id],
    queryFn: async () => {
      const result = await invoiceItemService.listByInvoice(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Line items pagination
  const lineItemsPagination = usePagination({
    storageKey: 'edit-invoice-line-items',
    persistToUrl: false,
    defaultPageSize: 25,
  });

  // Price book items pagination
  const pbPagination = usePagination({
    storageKey: 'edit-invoice-pricebook-items',
    persistToUrl: false,
    defaultPageSize: 25,
  });

  // Reset price book pagination when the price book changes
  // (covers navigating between invoices with different-sized price books)
  useEffect(() => {
    pbPagination.resetToFirstPage();
  }, [invoice?.price_book_id]);

  // Fetch price book items (only if invoice has a price book)
  const { data: pbData, isLoading: isLoadingPbItems, isFetching: isFetchingPbItems } = useQuery({
    queryKey: ['edit-invoice-pb-items', invoice?.price_book_id, pbPagination.pageIndex, pbPagination.pageSize, pbPagination.debouncedSearch],
    queryFn: async () => {
      const result = await priceBookItemService.listByPriceBookPaginated(
        invoice!.price_book_id!,
        { page: pbPagination.pageIndex, pageSize: pbPagination.pageSize },
        { search: pbPagination.debouncedSearch, isActive: true }
      );
      if (result.error) throw result.error;
      return { items: result.data, totalCount: result.totalCount };
    },
    enabled: !!invoice?.price_book_id,
    placeholderData: (prev) => prev,
  });

  const pbItems = pbData?.items ?? [];
  const pbTotalCount = pbData?.totalCount ?? 0;

  // Client-side filtered line items
  const filteredInvoiceItems = useMemo(() => {
    if (!lineItemsSearch.trim()) return invoiceItems;
    const term = lineItemsSearch.toLowerCase();
    return invoiceItems.filter(item =>
      (item.item_code && item.item_code.toLowerCase().includes(term)) ||
      (item.description && item.description.toLowerCase().includes(term)) ||
      (item.uom && item.uom.toLowerCase().includes(term))
    );
  }, [invoiceItems, lineItemsSearch]);

  const paginatedInvoiceItems = useMemo(() => {
    const start = lineItemsPagination.pageIndex * lineItemsPagination.pageSize;
    return filteredInvoiceItems.slice(start, start + lineItemsPagination.pageSize);
  }, [filteredInvoiceItems, lineItemsPagination.pageIndex, lineItemsPagination.pageSize]);

  // Service context
  const getServiceContext = useCallback((): ServiceContext => ({
    companyId: profile?.company_id!,
    userId: user?.id,
  }), [profile?.company_id, user?.id]);

  // ── Header Save ──
  const handleSaveHeader = async () => {
    if (!id || !profile?.company_id || !user?.id) return;

    const updateData: Record<string, unknown> = {};
    if (headerForm.vendor_id !== headerSnapshot.vendor_id) updateData.vendor_id = headerForm.vendor_id;
    if (headerForm.invoice_number !== headerSnapshot.invoice_number) updateData.invoice_number = headerForm.invoice_number || null;
    if (headerForm.invoice_date !== headerSnapshot.invoice_date) updateData.invoice_date = headerForm.invoice_date || null;
    if (headerForm.service_date !== headerSnapshot.service_date) updateData.service_date = headerForm.service_date || null;
    if (headerForm.payment_terms !== headerSnapshot.payment_terms) updateData.payment_terms = headerForm.payment_terms || null;
    if (headerForm.po_number !== headerSnapshot.po_number) updateData.po_number = headerForm.po_number || null;
    if (headerForm.notes !== headerSnapshot.notes) updateData.notes = headerForm.notes || null;

    if (Object.keys(updateData).length === 0) {
      setIsHeaderEditing(false);
      return;
    }

    const result = await invoiceService.update(
      id,
      updateData,
      headerSnapshot,
      getServiceContext()
    );

    if (result.error) {
      toast.error('Failed to save invoice header');
      return;
    }

    toast.success('Invoice header saved');
    setHeaderSnapshot({ ...headerSnapshot, ...updateData });
    setIsHeaderEditing(false);
    queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
  };

  // ── Status Change ──
  const handleStatusChange = async (newStatusId: string, _statusName: string, comments?: string) => {
    if (!id || !profile?.company_id || !user?.id) return;

    const result = await invoiceService.updateStatus(
      id,
      newStatusId,
      invoice?.status_id || null,
      { companyId: profile.company_id, userId: user.id },
      { comments }
    );

    if (result.error) {
      toast.error('Failed to update status');
    } else {
      queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
      toast.success('Status updated');
    }
  };

  // ── Line Items: Inline Edit ──
  const PRICE_FIELDS = new Set(['quantity', 'unit_price', 'discount']);

  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent<InvoiceItem>) => {
    const { data: rowData, colDef, oldValue, newValue } = event;
    if (!rowData || oldValue === newValue || !id) return;

    const columnId = colDef.field as keyof InvoiceItem;
    const snapshot = { [columnId]: oldValue };
    const updateData = { [columnId]: newValue };

    // Optimistic UI: instantly recalc & display line_total from in-memory row data
    if (PRICE_FIELDS.has(columnId)) {
      const newLineTotal = calculateLineTotal(rowData.quantity, rowData.unit_price, rowData.discount);
      event.node.setDataValue('line_total', newLineTotal);
    }

    // Pass current row values so the service can skip a SELECT
    const currentValues = {
      quantity: rowData.quantity,
      unit_price: rowData.unit_price,
      discount: rowData.discount,
      line_total: rowData.line_total,
    };

    try {
      const result = await invoiceItemService.update(
        rowData.id,
        updateData,
        snapshot,
        id,
        getServiceContext(),
        currentValues
      );
      if (result.error) throw result.error;
      // Only refresh invoice header totals — grid is already up to date
      queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
    } catch {
      // Revert optimistic update on failure
      queryClient.invalidateQueries({ queryKey: ['invoice-items', id] });
      toast.error('Failed to update line item');
    }
  }, [id, getServiceContext, queryClient]);

  // ── Line Items: Delete Selected ──
  const handleDeleteItems = async () => {
    if (!id || !profile?.company_id || !user?.id) return;
    setIsDeleting(true);
    try {
      const itemIds = selectedLineItems.map(item => item.id);
      const result = await invoiceItemService.deleteBatch(itemIds, id, getServiceContext());
      if (result.error) throw result.error;
      toast.success(`Deleted ${itemIds.length} item${itemIds.length !== 1 ? 's' : ''}`);
      setSelectedLineItems([]);
      queryClient.invalidateQueries({ queryKey: ['invoice-items', id] });
      queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
    } catch {
      toast.error('Failed to delete items');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // ── Add Price Book Item to Invoice ──
  const handleAddPriceBookItem = useCallback(async (pbItem: PriceBookItem) => {
    if (!id || !profile?.company_id || !user?.id) return;

    const newItem = {
      invoice_id: id,
      item_code: pbItem.itemcode,
      description: pbItem.description,
      unit_price: pbItem.price,
      discount: pbItem.discount,
      uom: pbItem.unit,
      price_book_item_id: pbItem.id,
      quantity: 1,
    };

    const result = await invoiceItemService.create(newItem, getServiceContext());
    if (result.error) {
      toast.error('Failed to add item');
      return;
    }
    toast.success('Item added');
    queryClient.invalidateQueries({ queryKey: ['invoice-items', id] });
    queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
  }, [id, getServiceContext, queryClient, profile?.company_id, user?.id]);

  // ── Price Book Search: Enter to add first result ──
  const handlePbSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && pbItems.length > 0 && pbPagination.searchValue.trim()) {
        e.preventDefault();
        handleAddPriceBookItem(pbItems[0]);
        pbPagination.setSearchValue('');
      }
    },
    [pbItems, pbPagination, handleAddPriceBookItem]
  );

  const pbGridContext = useMemo(
    () => ({
      searchActive: pbSearchFocused && !!pbPagination.searchValue.trim(),
    }),
    [pbSearchFocused, pbPagination.searchValue]
  );

  // ── Add Non-Contract Item to Invoice ──
  const handleAddNonContractItem = useCallback(async () => {
    if (!id || !profile?.company_id || !user?.id) return;

    const newItem = {
      invoice_id: id,
      item_code: nonContractForm.item_code || null,
      description: nonContractForm.description,
      quantity: nonContractForm.quantity,
      uom: nonContractForm.uom || null,
      unit_price: nonContractForm.unit_price,
      discount: nonContractForm.discount,
    };

    const result = await invoiceItemService.create(newItem, getServiceContext());
    if (result.error) {
      toast.error('Failed to add item');
      return;
    }
    toast.success('Item added');
    queryClient.invalidateQueries({ queryKey: ['invoice-items', id] });
    queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
    setNonContractForm({
      item_code: '',
      description: '',
      quantity: 1,
      uom: '',
      unit_price: 0,
      discount: 0,
    });
  }, [id, nonContractForm, getServiceContext, queryClient, profile?.company_id, user?.id]);

  // ── Line Items Column Defs ──
  const lineItemColumnDefs = useMemo<ColDef<InvoiceItem>[]>(() => [
    {
      field: 'item_code',
      headerName: 'Item Code',
      minWidth: 120,
      editable: (params) => !isReadonly && !params.data?.price_book_item_id,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      editable: (params) => !isReadonly && !params.data?.price_book_item_id,
    },
    {
      field: 'quantity',
      headerName: 'Qty',
      width: 90,
      editable: !isReadonly,
      cellEditor: 'agNumberCellEditor',
      type: 'rightAligned',
    },
    {
      field: 'discount',
      headerName: 'Disc %',
      width: 100,
      editable: (params) => !isReadonly && !params.data?.price_book_item_id,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { precision: 2, min: 0, max: 100 },
      type: 'rightAligned',
      valueFormatter: (params: ValueFormatterParams<InvoiceItem, number>) =>
        params.value != null ? `${params.value}%` : '',
    },
    {
      field: 'uom',
      headerName: 'UoM',
      width: 90,
      editable: (params) => !isReadonly && !params.data?.price_book_item_id,
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      width: 120,
      editable: (params) => !isReadonly && !params.data?.price_book_item_id,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: { precision: 4 },
      type: 'rightAligned',
      valueFormatter: (params: ValueFormatterParams<InvoiceItem, number>) =>
        params.value != null ? formatCurrency(params.value) : '',
    },
    {
      field: 'line_total',
      headerName: 'Ext Amt',
      width: 120,
      editable: false,
      type: 'rightAligned',
      valueFormatter: (params: ValueFormatterParams<InvoiceItem, number>) =>
        params.value != null ? formatCurrency(params.value) : '',
    },
  ], [isReadonly]);

  // ── Price Book Items Column Defs ──
  const AddButtonRenderer = useCallback((params: ICellRendererParams<PriceBookItem>) => {
    if (!params.data) return null;
    const isHighlighted = params.context?.searchActive && params.node.rowIndex === 0;
    return (
      <Button
        variant={isHighlighted ? "default" : "ghost"}
        size="icon"
        className={cn("h-7 w-7", isHighlighted && "shadow-sm")}
        onClick={(e) => {
          e.stopPropagation();
          handleAddPriceBookItem(params.data!);
        }}
        title={isHighlighted ? "Press Enter to add (or click)" : "Add to invoice"}
      >
        <Plus className="w-4 h-4" />
      </Button>
    );
  }, [handleAddPriceBookItem]);

  const pbColumnDefs = useMemo<ColDef<PriceBookItem>[]>(() => [
    {
      headerName: '',
      width: 50,
      cellRenderer: AddButtonRenderer,
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
    },
    { field: 'itemcode', headerName: 'Code', minWidth: 100 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 150 },
    {
      headerName: "Disc'd Price",
      width: 120,
      type: 'rightAligned',
      valueGetter: (p) => p.data ? p.data.price * (1 - p.data.discount / 100) : null,
      valueFormatter: (params: ValueFormatterParams<PriceBookItem, number>) =>
        params.value != null ? formatCurrency(params.value) : '',
    },
    { field: 'unit', headerName: 'UoM', width: 80 },
  ], [AddButtonRenderer]);

  // ── Vendor name helper ──
  const selectedVendorName = vendors.find(v => v.id === headerForm.vendor_id)?.name
    || invoice?.vendor?.name
    || 'No vendor';

  // ── Loading / Not found ──
  if (isLoadingInvoice) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Invoice not found.</p>
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/invoices')}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Invoices
      </Button>

      {/* ── Top Section: Header (75%) + Status Actions (25%) ── */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Collapsible Header Card */}
        <div className="lg:w-[75%]">
          <Collapsible open={isHeaderOpen} onOpenChange={setIsHeaderOpen}>
            <div className="bg-card border border-border rounded-lg">
              <CollapsibleTrigger asChild>
                <button className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors rounded-lg">
                  {isHeaderOpen ? (
                    <div className="flex items-start gap-4 text-left">
                      <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="w-7 h-7 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h1 className="text-2xl font-bold text-foreground">
                            {invoice.invoice_number || 'Untitled Invoice'}
                          </h1>
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {selectedVendorName}
                          {invoice.price_book && (
                            <span> | Price Book: {invoice.price_book.name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-lg font-semibold text-foreground">
                        {selectedVendorName}
                        <span className="text-muted-foreground font-normal"> | </span>
                        {invoice.invoice_number || '—'}
                        <span className="text-muted-foreground font-normal"> | </span>
                        <span className="text-muted-foreground font-normal">{formatDate(invoice.invoice_date)}</span>
                        <span className="text-muted-foreground font-normal"> | </span>
                        <span className="text-muted-foreground font-normal">{invoice.current_status?.name || 'Unknown'}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {!isReadonly && (
                      isHeaderEditing ? (
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); handleSaveHeader(); }}>
                          <Save className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setIsHeaderEditing(true); }}>
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )
                    )}
                    {isHeaderOpen ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="px-6 pb-6 pt-0 border-t border-border mx-6">
                  {isHeaderEditing && !isReadonly ? (
                    /* ── Edit Mode ── */
                    <div className="pt-6 space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Vendor Picker */}
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Vendor</label>
                          <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" className="w-full justify-between">
                                <span className="truncate">
                                  {headerForm.vendor_id
                                    ? vendors.find(v => v.id === headerForm.vendor_id)?.name || 'Select vendor...'
                                    : 'Select vendor...'}
                                </span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[350px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search vendors..." />
                                <CommandList>
                                  <CommandEmpty>No vendor found.</CommandEmpty>
                                  <CommandGroup>
                                    {vendors.map((vendor) => (
                                      <CommandItem
                                        key={vendor.id}
                                        value={vendor.name}
                                        onSelect={() => {
                                          setHeaderForm(f => ({ ...f, vendor_id: vendor.id }));
                                          setVendorPopoverOpen(false);
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", headerForm.vendor_id === vendor.id ? "opacity-100" : "opacity-0")} />
                                        {vendor.name}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Invoice Number</label>
                          <Input
                            value={headerForm.invoice_number}
                            onChange={(e) => setHeaderForm(f => ({ ...f, invoice_number: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Invoice Date</label>
                          <Input
                            type="date"
                            value={headerForm.invoice_date}
                            onChange={(e) => setHeaderForm(f => ({ ...f, invoice_date: e.target.value }))}
                          />
                        </div>
                        {/* Notes - spans all rows in the 4th column */}
                        <div className="md:row-span-3 flex flex-col">
                          <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                          <Textarea
                            className="flex-1 min-h-0 resize-none"
                            value={headerForm.notes}
                            onChange={(e) => setHeaderForm(f => ({ ...f, notes: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Payment Terms</label>
                          <Input
                            value={headerForm.payment_terms}
                            onChange={(e) => setHeaderForm(f => ({ ...f, payment_terms: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">PO Number</label>
                          <Input
                            value={headerForm.po_number}
                            onChange={(e) => setHeaderForm(f => ({ ...f, po_number: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">Service Date</label>
                          <Input
                            type="date"
                            value={headerForm.service_date}
                            onChange={(e) => setHeaderForm(f => ({ ...f, service_date: e.target.value }))}
                          />
                        </div>
                      </div>

                    </div>
                  ) : (
                    /* ── View Mode ── */
                    <div className="pt-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <ReadOnlyField label="Vendor" value={selectedVendorName} />
                        <ReadOnlyField label="Invoice Number" value={invoice.invoice_number} />
                        <ReadOnlyField label="Invoice Date" value={formatDate(invoice.invoice_date)} />
                        <div className="md:row-span-3">
                          <ReadOnlyField label="Notes" value={invoice.notes} />
                        </div>
                        <ReadOnlyField label="Payment Terms" value={invoice.payment_terms} />
                        <ReadOnlyField label="PO Number" value={invoice.po_number} />
                        <ReadOnlyField label="Service Date" value={formatDate(invoice.service_date)} />
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        {/* Status Actions */}
        <div className="lg:w-[25%]">
          <div className="bg-card border border-border rounded-lg p-6">
            <EntityStatusActions
              entityType="invoice"
              currentStatusId={invoice.status_id}
              onStatusChange={handleStatusChange}
            />
          </div>
        </div>
      </div>

      {/* ── Bottom Section: Item Picker (left) + Line Items (right) ── */}
      {isReadonly ? (
        /* Readonly: full-width line items only */
        <div>
          <Tabs value={rightPanelTab} onValueChange={setRightPanelTab} className="w-full">
            <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-4">
              <TabsTrigger value="line-items" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">
                Line Items
              </TabsTrigger>
              <TabsTrigger value="attachments" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">
                Attachments
              </TabsTrigger>
            </TabsList>

            <TabsContent value="line-items">
              <AGDataGrid<InvoiceItem>
                rowData={paginatedInvoiceItems}
                columnDefs={lineItemColumnDefs}
                totalCount={filteredInvoiceItems.length}
                pageIndex={lineItemsPagination.pageIndex}
                pageSize={lineItemsPagination.pageSize}
                onPageChange={lineItemsPagination.setPageIndex}
                onPageSizeChange={lineItemsPagination.setPageSize}
                searchValue={lineItemsSearch}
                searchPlaceholder="Search line items..."
                onSearchChange={setLineItemsSearch}
                isLoading={isLoadingItems}
                isEditMode={false}
                showEditToggle={false}
                height="calc(100vh - 520px)"
                onCellValueChanged={handleCellValueChanged}
              />
            </TabsContent>

            <TabsContent value="attachments">
              <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
                Attachments coming soon
              </div>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        /* Editable: resizable split */
        <ResizablePanelGroup direction="horizontal">
          {/* Left Panel: Item Picker */}
          <ResizablePanel defaultSize={25} minSize={15}>
            <div className="pr-2">
              <Tabs value={leftPanelTab} onValueChange={setLeftPanelTab} className="w-full">
                <div className="flex items-center mb-4">
                  <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0">
                    <TabsTrigger value="pricebook" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-3 text-sm">
                      Price Book Items
                    </TabsTrigger>
                    <TabsTrigger value="non-contract" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-3 py-3 text-sm">
                      Non-Contract
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="pricebook">
                  {invoice.price_book_id ? (
                    <AGDataGrid<PriceBookItem>
                      rowData={pbItems}
                      columnDefs={pbColumnDefs}
                      totalCount={pbTotalCount}
                      pageIndex={pbPagination.pageIndex}
                      pageSize={pbPagination.pageSize}
                      searchValue={pbPagination.searchValue}
                      searchPlaceholder="Search for items to add to invoice..."
                      isLoading={isLoadingPbItems}
                      isFetching={isFetchingPbItems}
                      isEditMode={false}
                      showEditToggle={false}
                      height="calc(100vh - 520px)"
                      onSearchChange={pbPagination.setSearchValue}
                      onSearchKeyDown={handlePbSearchKeyDown}
                      onSearchFocus={() => setPbSearchFocused(true)}
                      onSearchBlur={() => setPbSearchFocused(false)}
                      gridContext={pbGridContext}
                      onPageChange={pbPagination.setPageIndex}
                      onPageSizeChange={pbPagination.setPageSize}
                    />
                  ) : (
                    <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
                      No price book assigned to this invoice.
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="non-contract">
                  <div className="bg-card border border-border rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Item Code</label>
                      <Input
                        value={nonContractForm.item_code}
                        onChange={(e) => setNonContractForm(f => ({ ...f, item_code: e.target.value }))}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">Description *</label>
                      <Input
                        value={nonContractForm.description}
                        onChange={(e) => setNonContractForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Required"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Qty</label>
                        <Input
                          type="number"
                          value={nonContractForm.quantity}
                          onChange={(e) => setNonContractForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">UoM</label>
                        <Input
                          value={nonContractForm.uom}
                          onChange={(e) => setNonContractForm(f => ({ ...f, uom: e.target.value }))}
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Price</label>
                        <Input
                          type="number"
                          value={nonContractForm.unit_price}
                          onChange={(e) => setNonContractForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))}
                          onBlur={() => setNonContractForm(f => ({ ...f, unit_price: parseFloat(f.unit_price.toFixed(2)) }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Disc %</label>
                        <Input
                          type="number"
                          value={nonContractForm.discount}
                          onChange={(e) => setNonContractForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))}
                          onBlur={() => setNonContractForm(f => ({ ...f, discount: parseFloat(f.discount.toFixed(2)) }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground mb-1 block">Ext Amt</label>
                        <div className="h-9 flex items-center text-sm text-foreground">
                          {formatCurrency(nonContractForm.quantity * (1 - nonContractForm.discount / 100) * nonContractForm.unit_price)}
                        </div>
                      </div>
                    </div>
                    <Button
                      className="w-full"
                      disabled={!nonContractForm.description.trim()}
                      onClick={handleAddNonContractItem}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item to Invoice
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Right Panel: Line Items */}
          <ResizablePanel defaultSize={75} minSize={40}>
            <div className="pl-2">
              <Tabs value={rightPanelTab} onValueChange={setRightPanelTab} className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <TabsList className="w-auto justify-start border-b border-border rounded-none bg-transparent h-auto p-0">
                    <TabsTrigger value="line-items" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">
                      Line Items
                    </TabsTrigger>
                    <TabsTrigger value="attachments" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">
                      Attachments
                    </TabsTrigger>
                  </TabsList>

                  {/* Actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        Actions
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setShowUploadTicketDialog(true)}>
                        <FileCheck className="w-4 h-4 mr-2" />
                        Upload Field Ticket
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        disabled={selectedLineItems.length === 0}
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Selected ({selectedLineItems.length})
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <TabsContent value="line-items">
                  <AGDataGrid<InvoiceItem>
                    rowData={paginatedInvoiceItems}
                    columnDefs={lineItemColumnDefs}
                    totalCount={filteredInvoiceItems.length}
                    pageIndex={lineItemsPagination.pageIndex}
                    pageSize={lineItemsPagination.pageSize}
                    onPageChange={lineItemsPagination.setPageIndex}
                    onPageSizeChange={lineItemsPagination.setPageSize}
                    searchValue={lineItemsSearch}
                    searchPlaceholder="Search line items..."
                    onSearchChange={setLineItemsSearch}
                    isLoading={isLoadingItems}
                    isEditMode={lineItemsEditMode}
                    showEditToggle={true}
                    height="calc(100vh - 560px)"
                    onEditModeChange={setLineItemsEditMode}
                    onCellValueChanged={handleCellValueChanged}
                    rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
                    onSelectionChanged={(event: SelectionChangedEvent<InvoiceItem>) => {
                      setSelectedLineItems(event.api.getSelectedRows());
                    }}
                  />
                </TabsContent>

                <TabsContent value="attachments">
                  <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground">
                    Attachments coming soon
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      )}

      {/* Upload Field Ticket Dialog */}
      <UploadFieldTicketDialog
        open={showUploadTicketDialog}
        onOpenChange={setShowUploadTicketDialog}
        invoiceId={id!}
        vendorId={invoice?.vendor_id || null}
        priceBookId={invoice?.price_book_id || null}
        companyId={profile?.company_id!}
        userId={user?.id!}
        onTicketSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['invoice-edit', id] });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Items</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLineItems.length} item{selectedLineItems.length !== 1 ? 's' : ''}? This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItems}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Simple read-only field display */
function ReadOnlyField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}
